import { useState, type FormEvent } from 'react';
import {
  Box,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Button,
  Typography,
  Chip,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type {
  CreateSkillDto,
  Skill,
  SkillCategory,
  SkillExample,
  UpdateSkillDto,
} from '@/types/domain';
import { CATEGORIES, SUGGESTED_CAPABILITIES } from '../constants';

export interface SkillFormProps {
  mode: 'create' | 'edit';
  initialData?: Skill | undefined;
  onSubmit: (data: CreateSkillDto | UpdateSkillDto) => void | Promise<void>;
  onCancel: () => void;
  onError: (error: string) => void;
}

/**
 * 技能表单组件
 * 支持创建和编辑两种模式
 */
export default function SkillForm({ mode, initialData, onSubmit, onCancel, onError }: SkillFormProps) {
  const { t } = useTranslation();

  const [id, setId] = useState(initialData?.id || '');
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [category, setCategory] = useState<SkillCategory>(initialData?.category || 'custom');
  const [version, setVersion] = useState(initialData?.version || '1.0.0');
  const [author, setAuthor] = useState(initialData?.author || '');
  const [tags, setTags] = useState(initialData?.tags?.join(', ') || '');
  const [capabilities, setCapabilities] = useState(initialData?.capabilities?.join(', ') || '');
  const [dependencies, setDependencies] = useState(initialData?.dependencies?.join(', ') || '');
  const [priority, setPriority] = useState(initialData?.priority ?? 50);
  const [example, setExample] = useState(initialData?.example || '');
  const [instructions, setInstructions] = useState(initialData?.instructions || '');
  const [tools, setTools] = useState(initialData?.tools?.join(', ') || '');
  const [examples, setExamples] = useState<SkillExample[]>(
    initialData?.examples?.map((item): SkillExample => ({
      input: item.input ?? '',
      output: item.output ?? '',
      ...(item.description ? { description: item.description } : {}),
    })) ?? []
  );

  const parseCsv = (value: string): string[] => {
    if (!value) {
      return [];
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const addExample = () => {
    setExamples((prev) => [...prev, { input: '', output: '' }]);
  };

  const removeExample = (index: number) => {
    setExamples((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateExample = (index: number, key: keyof SkillExample, value: string) => {
    setExamples((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) {
          return item;
        }

        if (key === 'description') {
          return {
            input: item.input,
            output: item.output,
            ...(value ? { description: value } : {}),
          };
        }

        return { ...item, [key]: value };
      })
    );
  };

  const addCapability = (capability: string) => {
    const current = parseCsv(capabilities);
    if (current.includes(capability)) {
      return;
    }
    setCapabilities([...current, capability].join(', '));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (mode === 'create' && !id) {
      onError(t('skills.idRequired'));
      return;
    }

    if (mode === 'create') {
      const idRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
      if (!idRegex.test(id)) {
        onError(t('skills.idFormatError'));
        return;
      }
    }

    if (!name || !instructions) {
      onError('Name and instructions are required.');
      return;
    }

    try {
      const baseData: UpdateSkillDto = {
        name,
        description,
        category,
        version,
        priority,
        example,
        instructions,
        tags: parseCsv(tags),
        capabilities: parseCsv(capabilities),
        tools: parseCsv(tools),
        examples: examples.filter((item) => item.input || item.output),
      };

      if (author) {
        baseData.author = author;
      }

      if (dependencies) {
        baseData.dependencies = parseCsv(dependencies);
      }

      if (mode === 'create') {
        const createData: CreateSkillDto = {
          id,
          name,
          description,
          category,
          version,
          priority,
          example,
          instructions,
          tags: parseCsv(tags),
          capabilities: parseCsv(capabilities),
          tools: parseCsv(tools),
          examples: examples.filter((item) => item.input || item.output),
          ...(author ? { author } : {}),
          ...(dependencies ? { dependencies: parseCsv(dependencies) } : {}),
        };
        await onSubmit(createData);
        return;
      }

      await onSubmit(baseData);
    } catch {
      // Parent handles request error display.
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('skills.basicInfo')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('skills.id')}
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  disabled={mode === 'edit'}
                  helperText={mode === 'edit' ? t('skills.idNotChangeable') : t('skills.idHelper')}
                  required={mode === 'create'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('skills.name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('skills.description')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('skills.category')}</InputLabel>
                  <Select
                    value={category}
                    label={t('skills.category')}
                    onChange={(e) => setCategory(e.target.value as SkillCategory)}
                  >
                    {CATEGORIES.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('skills.version')}
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('skills.author')}
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  {t('skills.priority')}: {priority}
                </Typography>
                <Slider
                  value={priority}
                  onChange={(_, value) => setPriority(value as number)}
                  min={1}
                  max={100}
                  marks
                  valueLabelDisplay="auto"
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('skills.tagsCapabilities')}
            </Typography>

            <TextField
              fullWidth
              label={t('skills.tags')}
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              helperText="Comma-separated tags"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('skills.capabilities')}
              value={capabilities}
              onChange={(e) => setCapabilities(e.target.value)}
              helperText="Comma-separated capabilities, e.g. file:read, agent:execute"
              sx={{ mb: 2 }}
            />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Suggested capabilities:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {SUGGESTED_CAPABILITIES.map((capability) => (
                  <Chip
                    key={capability}
                    label={capability}
                    size="small"
                    variant="outlined"
                    onClick={() => addCapability(capability)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Dependent Skills"
              value={dependencies}
              onChange={(e) => setDependencies(e.target.value)}
              helperText="Comma-separated skill IDs"
              sx={{ mt: 2 }}
            />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Example
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              This text is shown in the skill details modal.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Example (Markdown)"
              value={example}
              onChange={(e) => setExample(e.target.value)}
            />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('skills.instructions')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              required
            />
          </Paper>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('skills.examples')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {examples.map((item, index) => (
                  <Paper key={index} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <TextField
                        fullWidth
                        label="Input"
                        value={item.input}
                        onChange={(e) => updateExample(index, 'input', e.target.value)}
                        size="small"
                      />
                      <Button size="small" color="error" onClick={() => removeExample(index)}>
                        Remove
                      </Button>
                    </Stack>
                    <TextField
                      fullWidth
                      label="Output"
                      value={item.output}
                      onChange={(e) => updateExample(index, 'output', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="Description"
                      value={item.description || ''}
                      onChange={(e) => updateExample(index, 'description', e.target.value)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                ))}
                <Button variant="outlined" onClick={addExample} sx={{ alignSelf: 'flex-start' }}>
                  Add Example
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('skills.tools')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                label="Related Tools"
                value={tools}
                onChange={(e) => setTools(e.target.value)}
                helperText="Comma-separated tool IDs"
                multiline
                rows={2}
              />
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" variant="contained">
          {mode === 'create' ? t('common.create') : t('common.save')}
        </Button>
      </Box>
    </Box>
  );
}
